/**
 * Core-100 R2 — CORE-BE: the reason a carried ticket is here, and (for the front office) a way
 * to state or correct it. The database decides who may (`state_carry_reason`).
 */
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CARRY_REASONS, CARRY_REASON_LABEL, describeCarryReason } from '@/lib/tickets/carryReason';
import { describeContinuationProvenance, type ContinuationProvenance } from '@/lib/rollover/continuationLabels';

interface Props {
  ticketId: string;
  reason: string | null | undefined;
  basis: string | null | undefined;
  canState: boolean;
}

export function CarryReasonLine({ ticketId, reason, basis, canState }: Props) {
  const [current, setCurrent] = useState({ reason: reason ?? null, basis: basis ?? null });
  const [error, setError] = useState<string | null>(null);
  // OCT20 WS-ROLL: the line used to initialise from props ONCE, so a reason stated elsewhere (another device, the
  // booking record, a realtime refresh of the card) kept showing "reason not recorded" until the card remounted.
  // The ticket row is the truth: whenever it changes, the line shows it.
  useEffect(() => {
    setCurrent({ reason: reason ?? null, basis: basis ?? null });
  }, [ticketId, reason, basis]);
  // OCT20A WS-ROLL2 (R5): which kind of continuation this is, and who approved it — the server classifies
  // (continuation_provenance); an overrun still awaiting approval is a separate line and never reads as authorized.
  const [prov, setProv] = useState<ContinuationProvenance | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await supabase.rpc('continuation_provenance' as never, { p_ticket_id: ticketId } as never);
        if (!cancelled) setProv((data ?? null) as ContinuationProvenance | null);
      } catch {
        if (!cancelled) setProv(null);
      }
    })();
    return () => { cancelled = true; };
  }, [ticketId, reason, basis]);
  const lines = describeContinuationProvenance(prov, (iso) => new Date(iso).toLocaleString());

  const state = async (next: string) => {
    setError(null);
    const { error: e } = await supabase.rpc('state_carry_reason' as never, { p_ticket_id: ticketId, p_reason: next } as never);
    if (e) { setError('Could not save the reason.'); return; }
    setCurrent({ reason: next, basis: 'stated' });
  };
  return (
    <div className="space-y-1" data-testid="carry-reason">
      {lines.label && <div className="font-semibold" data-testid="continuation-label">{lines.label}</div>}
      {lines.approval && <div className="opacity-80" data-testid="continuation-approval">{lines.approval}</div>}
      {lines.scope && <div className="opacity-80">{lines.scope}</div>}
      {lines.pending && <div className="font-medium text-red-700 dark:text-red-300" data-testid="continuation-pending">{lines.pending}</div>}
      <div>🔄 {describeCarryReason(current.reason, current.basis)}</div>
      {canState && (
        <Select value={current.basis === 'stated' && current.reason ? current.reason : undefined} onValueChange={(v) => void state(v)}>
          <SelectTrigger className="h-7 w-56 text-xs" aria-label="State why this ticket was carried over">
            <SelectValue placeholder="State the reason" />
          </SelectTrigger>
          <SelectContent>
            {CARRY_REASONS.map((r) => <SelectItem key={r} value={r}>{CARRY_REASON_LABEL[r]}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {error && <div className="text-destructive">{error}</div>}
    </div>
  );
}
